import { Link as RouterLink } from 'react-router-dom';
import { Text, makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  container: {
    flex: '1 1 auto',
    padding: '40px 16px',
    maxWidth: '720px',
    margin: '0 auto',
    width: '100%',
    '@media (max-width: 480px)': {
      padding: '24px 12px',
    },
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: tokens.shadow16,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    '@media (max-width: 480px)': {
      padding: '24px 16px',
    },
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '24px',
    fontSize: '14px',
    color: tokens.colorBrandBackground,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  title: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '24px',
    color: tokens.colorNeutralForeground1,
  },
  updated: {
    display: 'block',
    fontSize: '13px',
    opacity: 0.6,
    marginBottom: '24px',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: tokens.colorNeutralForeground1,
  },
  body: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: tokens.colorNeutralForeground2,
  },
  footer: {
    padding: '20px 16px',
    textAlign: 'center' as const,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '6px',
    fontSize: '12px',
    lineHeight: '1.6',
    opacity: 0.6,
  },
  footerLink: {
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
});

export default function AiDisclosure() {
  const styles = useStyles();

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.card}>
          <RouterLink to="/" className={styles.backLink}>← Back to Sign In</RouterLink>
          <Text className={styles.title}>AI Usage Disclosure</Text>
          <Text className={styles.updated}>Last updated: March 2026</Text>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>1. How We Use AI</Text>
            <p className={styles.body}>
              Sherpai uses artificial intelligence, powered by Azure OpenAI Service, to provide
              conversational support, goal tracking, knowledge retrieval, and personalised guidance
              to users within Microsoft Teams. The AI processes user messages in real time to generate
              helpful, context-aware responses.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>2. AI-Generated Content</Text>
            <p className={styles.body}>
              Responses provided by Sherpai are generated by AI and should be treated as suggestions,
              not authoritative advice. While we strive for accuracy, AI-generated content may
              occasionally contain errors or incomplete information. Users should verify critical
              information through appropriate channels and exercise professional judgement.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>3. Data and Training</Text>
            <p className={styles.body}>
              Your conversations with Sherpai are not used to train or fine-tune AI models. We use
              Azure OpenAI Service which provides enterprise-grade data protections, ensuring your
              data remains private to your organisation. Conversation data is processed solely to
              provide the service and generate aggregated, anonymised analytics.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>4. Human Oversight</Text>
            <p className={styles.body}>
              The Sherpai platform includes human oversight mechanisms. Organisation administrators
              can review aggregated analytics, configure AI behaviour through the Admin Portal, and
              curate the solution library that informs AI responses. Users always have the option to
              escalate to human support when needed.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>5. Transparency</Text>
            <p className={styles.body}>
              We are committed to transparency about how AI is used in our platform. Sherpai
              identifies itself as an AI assistant in conversations. We continuously monitor AI
              outputs for quality and safety, and we update our practices in line with evolving AI
              governance standards and regulations.
            </p>
          </div>

          <div className={styles.section}>
            <Text className={styles.sectionTitle}>6. Contact Us</Text>
            <p className={styles.body}>
              If you have questions about our use of AI or this disclosure, please contact your
              organisation&apos;s IT administrator or reach out to us at ai@component6.com.au.
            </p>
          </div>
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <RouterLink to="/privacy" className={styles.footerLink}>Privacy Policy</RouterLink>
          <span>·</span>
          <RouterLink to="/terms" className={styles.footerLink}>Terms of Service</RouterLink>
          <span>·</span>
          <RouterLink to="/ai-disclosure" className={styles.footerLink}>AI Disclosure</RouterLink>
          <span>·</span>
          <span>© 2026 Component6 Pty Ltd</span>
        </div>
      </footer>
    </div>
  );
}
